import React, {
  createContext,
  useContext,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useGet, usePut } from '../hooks';
import {
  GeneralRegularHours,
  StoreHoursResponse,
  UpdateStoreHoursRequest,
} from '@/types/generalHours';

interface GeneralHoursContextState {
  regularHours: GeneralRegularHours | null;
  /** Hours come from Location Master, so the SMP shows them read-only. */
  externallyManaged: boolean;
  loading: boolean;
  error: string | null;
  saving: boolean;
  fetchGeneralHours: () => void;
  updateGeneralHours: (
    regularHours: GeneralRegularHours,
    onComplete?: (success: boolean, error?: string) => void
  ) => void;
}

interface GeneralHoursProviderProps {
  children: ReactNode;
  restaurantId: number | null;
}

const GeneralHoursContext = createContext<GeneralHoursContextState | undefined>(
  undefined
);

export const GeneralHoursProvider: React.FC<GeneralHoursProviderProps> = ({
  children,
  restaurantId,
}) => {
  const {
    data,
    loading,
    error,
    refetch: fetchStoreHours,
  } = useGet<StoreHoursResponse>('', {}, false, true);

  const {
    loading: saving,
    error: saveError,
    refetch: putStoreHours,
  } = usePut<StoreHoursResponse>(null, undefined, {}, false, true);

  const saveOnCompleteRef = useRef<
    ((success: boolean, error?: string) => void) | null
  >(null);
  const savingPreviousRef = useRef(false);

  const hoursUrl = restaurantId
    ? process.env.REACT_APP_API_DOMAIN + `stores/${restaurantId}/hours`
    : null;

  const fetchGeneralHours = useCallback(() => {
    if (!hoursUrl) return;
    fetchStoreHours(hoursUrl);
  }, [hoursUrl, fetchStoreHours]);

  const updateGeneralHours = useCallback(
    (
      regularHours: GeneralRegularHours,
      onComplete?: (success: boolean, error?: string) => void
    ) => {
      if (!hoursUrl) {
        console.warn('Restaurant ID is not available');
        return;
      }
      saveOnCompleteRef.current = onComplete ?? null;
      const body: UpdateStoreHoursRequest = { regularHours };
      putStoreHours(hoursUrl, body);
    },
    [hoursUrl, putStoreHours]
  );

  // Report the outcome once the request settles, then reload the saved hours.
  useEffect(() => {
    if (savingPreviousRef.current && !saving) {
      const onComplete = saveOnCompleteRef.current;
      saveOnCompleteRef.current = null;
      if (onComplete) {
        onComplete(!saveError, saveError ?? undefined);
      }
      if (!saveError) {
        fetchGeneralHours();
      }
    }
    savingPreviousRef.current = saving;
  }, [saving, saveError, fetchGeneralHours]);

  const contextValue: GeneralHoursContextState = {
    regularHours: data?.regularHours ?? null,
    externallyManaged: data?.regularHoursExternallyManaged === true,
    loading,
    error,
    saving,
    fetchGeneralHours,
    updateGeneralHours,
  };

  return (
    <GeneralHoursContext.Provider value={contextValue}>
      {children}
    </GeneralHoursContext.Provider>
  );
};

export const useGeneralHours = (): GeneralHoursContextState => {
  const context = useContext(GeneralHoursContext);

  if (context === undefined) {
    throw new Error(
      'useGeneralHours must be used within a GeneralHoursProvider'
    );
  }

  return context;
};

export { GeneralHoursContext };
